import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import useAuthEffect from "../../hook/useAuthEffect";
import {
  fetchSuggestions,
  fetchTask,
  rowModified,
  taskRowSelected,
  splitTaskRowGender,
} from "../../redux/ducks/task";
import { Table } from "../dumb/DataTableTask";
import TaskEditorRow from "./TaskEditorRow";
import useDebounce from "../../hook/useDebounce";

const emptyArray = [];

const TaskEditor = (props) => {
  const [debounce] = useDebounce(500);
  const [suggestionDebounce] = useDebounce(250);
  const dispatch = useDispatch();

  const task = useSelector((state) => state.task.tasks[props.taskID]);
  const isSaved = task?.isSaved;
  const suggestions = task?.suggestions;

  const [rowsArray, setRowsArray] = useState([]);
  const [rowsObject, setRowsObject] = useState({});
  const [prevRows, setPrevRows] = useState([]);
  const [selectedRow, setSelectedRow] = useState(0);
  const selectedRowRef = useRef(selectedRow);
  const [confirmReq, setConfirmReq] = useState({ rowID: null, nonce: 0 });
  const [rowHasErrors, setRowHasErrors] = useState({}); // { [rowID]: boolean }

  const onErrorStateChange = (rowID, hasErr) => {
    const key = String(rowID);
    setRowHasErrors((prev) => ({
      ...prev,
      [key]: !!hasErr,
    }));
  };

  /* Fetch task on load */
  useAuthEffect(() => {
    if (!task?.rows) dispatch(fetchTask({ taskID: props.taskID }));

    const loadQaErrors = async () => {
      try {
        const res = await fetch(
          `${window.API_URL}/task/getQArow.php?taskID=${props.taskID}`,
        );
        const data = await res.json();
        // expects: [{ rowID, hasErrors: bool }, ...]
        const errorMap = {};
        (data || []).forEach(({ rowID, hasErrors, qa_status }) => {
          errorMap[String(rowID)] = !!hasErrors;
        });
        setRowHasErrors(errorMap);
      } catch (e) {
        console.error("Failed to load QA results:", e);
      }
    };

    loadQaErrors();
  }, []);

  /* Warn user of unsaved changes */
  window.onbeforeunload = (e) => {
    if (!isSaved && isSaved !== undefined) return "Unsaved changes.";
  };

  /* Keep selected row ref updated */
  useEffect(() => {
    selectedRowRef.current = selectedRow;
  });

  /* Format rows - create both array (for selection) and object (for DataTable) */
  useEffect(() => {
    if (task?.rows) {
      setPrevRows([...rowsArray]);

      const formattedArray = [];
      const formattedObject = {};

      Object.keys(task.rows).forEach((rowID, index) => {
        const normalize = (v) => {
          if (Array.isArray(v)) return v.length > 0;
          if (typeof v === "number") return v > 0;
          if (typeof v === "string")
            return v === "1" || v.toLowerCase() === "true";
          return !!v;
        };
        const rowFromApi = task.rows[rowID];
        const prev = rowsObject[rowID]; // keep any locally-updated value
        const rawHasErrors = rowFromApi.hasErrors;
        const commentText = rowFromApi.comment?.text ?? "";

        const rowData = {
          index,
          rowID,
          source: task.rows[rowID].source,
          target: task.rows[rowID].target,
          completed: task.rows[rowID].completed,
          modified: task.rows[rowID].modified,
          charLimit: task.rows[rowID].charLimit,
          gender: task.rows[rowID].gender,
          sortNum: task.rows[rowID].sortNum,
          hasErrors:
            rawHasErrors === undefined || rawHasErrors === null
              ? (prev?.hasErrors ?? false)
              : normalize(rawHasErrors),
          hasComment: commentText.trim().length > 0,
        };

        formattedArray.push(rowData);
        formattedObject[rowID] = rowData; // Use rowID as key for DataTable
      });

      setRowsArray(formattedArray);
      setRowsObject(formattedObject);
    }
  }, [task?.rows]);

  /* Keyboard navigation */
  useEffect(() => {
    const handleKeyDown = (e) => {
      const tag = e.target.tagName.toLowerCase();
      const modKey = e.ctrlKey || e.metaKey; // ✅ Ctrl on Win/Linux, Command on Mac

      // Ignore *normal typing* in inputs/textareas, but keep shortcuts
      if (
        (tag === "input" || tag === "textarea" || e.target.isContentEditable) &&
        !modKey
      ) {
        return;
      }

      // Undo: Ctrl/Cmd + Z
      /*if (modKey && e.key.toLowerCase() === "z" && !e.shiftKey) {
        e.preventDefault();
        console.log("Undo triggered");
        return;
      }*/
      if (modKey && e.key.toLowerCase() === "z" && !e.shiftKey) {
        console.log("Undo triggered");
        return;
      }

      // Redo: Ctrl/Cmd + Shift + Z  OR  Ctrl/Cmd + Y
      if (
        (modKey && e.key.toLowerCase() === "z" && e.shiftKey) ||
        (modKey && e.key.toLowerCase() === "y")
      ) {
        e.preventDefault();
        console.log("Redo triggered");
        return;
      }

      const row = rowsArray[selectedRow];
      if (!row) return;

      // Let's use modKey + Arrow for cross-platform:
      if (modKey && e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedRow((r) => Math.max(0, r - 1));
        return;
      }

      if (modKey && e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedRow((r) => Math.min(rowsArray.length - 1, r + 1));
        return;
      }

      // Confirm segment: Ctrl/Cmd + Enter
      if (modKey && e.key === "Enter") {
        e.preventDefault();

        // If already completed -> just uncomplete
        if (Number(row.completed) === 1) {
          dispatch(
            rowModified({
              taskID: props.taskID,
              rowID: row.rowID,
              completed: 0,
            }),
          );
          return;
        }

        // If not completed -> request "complete + spellcheck" in the row component
        setConfirmReq({ rowID: row.rowID, nonce: Date.now() });
        return;
      }

      // Grab source: Ctrl/Cmd + /
      // Confirm segment: Ctrl/Cmd + Enter
      if (modKey && e.key === "Enter") {
        e.preventDefault();

        const checkboxId = "check" + row.sortNum; // must match TaskEditorRow: id={"check" + props.ID}
        const checkbox = document.getElementById(checkboxId);

        if (checkbox) {
          checkbox.click(); // triggers TaskEditorRow onChange => setCompleted + spellCheck
        } else {
          // fallback (shouldn't happen unless id scheme changes)
          const completed = row.completed === 1 ? 0 : 1;
          dispatch(
            rowModified({
              taskID: props.taskID,
              rowID: row.rowID,
              completed,
            }),
          );
        }

        return;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [selectedRow, rowsArray, dispatch, props.taskID]);

  const shouldRecalculate = () =>
    !prevRows.length || prevRows.length !== rowsArray.length;

  const onTargetInput = (target, row) => {
    if (row.completed)
      dispatch(
        rowModified({ taskID: props.taskID, rowID: row.rowID, completed: 0 }),
      );
    debounce(() =>
      dispatch(rowModified({ taskID: props.taskID, rowID: row.rowID, target })),
    );
  };

  const rowSelected = (row) => {
    dispatch(taskRowSelected({ taskID: props.taskID, rowID: row.rowID }));
    setSelectedRow(row.index);
  };

  const rowCompleted = (completed, row) => {
    dispatch(
      rowModified({ taskID: props.taskID, rowID: row.rowID, completed }),
    );
    if (completed) setSelectedRow((r) => r + 1);
  };

  // Update error state in local state when TaskEditorRow reports changes
  const handleErrorStateChange = (rowID, hasError) => {
    setRowsObject((prevRows) => ({
      ...prevRows,
      [rowID]: {
        ...prevRows[rowID],
        hasErrors: hasError,
      },
    }));

    setRowsArray((prevArray) =>
      prevArray.map((r) =>
        r.rowID === rowID ? { ...r, hasErrors: hasError } : r,
      ),
    );
  };
  const renderRow = (row) => (
    <TaskEditorRow
      confirmNonce={confirmReq.rowID === row.rowID ? confirmReq.nonce : 0}
      confirmPending={confirmReq.rowID === row.rowID}
      onConfirmDone={(rowID, nonce) =>
        setConfirmReq((cur) =>
          cur.rowID === rowID && cur.nonce === nonce
            ? { rowID: null, nonce: 0 }
            : cur,
        )
      }
      targetLang={task?.targetLang}
      targetLangIso={task?.targetLangIso}
      gender={row.gender}
      charLimit={row.charLimit}
      selected={row.index === selectedRow}
      key={row.rowID}
      taskID={props.taskID}
      ID={row.sortNum}
      source={row.source}
      target={row.target}
      completed={row.completed}
      modified={row.modified}
      rowID={row.rowID}
      hasErrors={
        rowHasErrors[String(row.rowID)] !== undefined
          ? !!rowHasErrors[String(row.rowID)]
          : !!row.hasErrors // ← use API value on first render/refresh
      }
      onErrorStateChange={handleErrorStateChange}
      //hasErrors={row.hasErrors}
      onTargetInput={(target) => onTargetInput(target, row)}
      setCompleted={(completed) => rowCompleted(completed, row)}
      rowSelected={() => rowSelected(row)}
      // highlightWords={
      //   row.index === selectedRow
      //     ? suggestions?.glossary?.map((g) => g[0])
      //     : emptyArray
      // }
      // tmMatchWords={
      //   row.index === selectedRow
      //     ? suggestions?.glossary?.map((g) => g[1])
      //     : emptyArray
      //}
      highlightWords={suggestions?.glossary?.map((g) => g[0]) || emptyArray}
      tmMatchWords={suggestions?.glossary?.map((g) => g[1]) || emptyArray}
      suggestionsLoaded={
        suggestions?.glossary !== undefined && suggestions?.glossary !== null
      }
      genderSplit={() => {
        if (row.gender === "N") {
          dispatch(
            splitTaskRowGender({ taskID: props.taskID, rowID: row.rowID }),
          );
        }
      }}
    />
  );

  // Fetch suggestions on row change
  /* useEffect(() => {
    suggestionDebounce(() =>
      dispatch(fetchSuggestions({ rowID: rowsArray[selectedRow]?.rowID }))
    );
  }, [selectedRow]);*/

  useEffect(() => {
    const currentRow = rowsArray[selectedRow];
    if (!currentRow) return;

    //  notify parent (TranslationRoute) about the active rowID
    if (props.onRowChange) {
      props.onRowChange(currentRow.rowID);
    }

    //  existing behaviour: fetch suggestions for this row
    suggestionDebounce(() =>
      dispatch(
        fetchSuggestions({ taskID: props.taskID, rowID: currentRow.rowID }),
      ),
    );
  }, [selectedRow, rowsArray]); // include rowsArray too

  return (
    <div className="task-editor__container">
      <Table
        shouldRecalculateOnRowChange={shouldRecalculate}
        customRow={true}
        renderRow={renderRow}
        rows={rowsObject}
        cols={[
          { name: "#", sortCol: "sortNum" },
          { name: "source", sortCol: "source", class: "table__header--source" },
          {
            name: `target (${task?.targetLang})`,
            class: "table__header--target",
          },
          { name: "g" },
          { name: "limits" },
          { name: "" },
          { name: "" },
        ]}
      />
    </div>
  );
};

export default TaskEditor;
